import { css } from "styled-components";
import {
  getDataFromLocalStorage,
  getObjectFromLocalStorage,
  removeDataFromLocalStorage,
  saveDataToLocalStorage,
  saveObjectToLocalStorage,
} from "../api/localStorage";
import {
  AS_COMPANY_ADMIN_USER_ROLES,
  CRMS,
  FSC_STEP_AUTOMATION_LEVEL,
  FSC_STEP_OFFSET_UNIT_LABEL,
  FSC_STEP_TYPE,
  LAST_LOGIN_METHOD_LS_KEY,
  SPREADSHEET_TYPES,
  SUBSCRIPTION_PLAN_NAME,
  USER_ROLES,
} from "./constants";
import { isEmpty } from "lodash";

const fileToBase64 = (file, callback) => {
  const reader = new FileReader();

  reader.onloadend = () => {
    const base64String = reader.result;
    callback(base64String);
  };

  reader.readAsDataURL(file);
};

const displayNumber = (val) => {
  if (typeof val === "number") {
    return val.toLocaleString("en-US");
  }
  return "";
};

const displayPercent = (val) => {
  if (typeof val === "number") {
    return `${Number(val.toFixed(2)).toLocaleString("en-US")}%`;
  }
  return "";
};

const displayCurrency = (amountInCents, currency) => {
  if (typeof amountInCents !== "number") {
    return "";
  }

  const value = amountInCents / 100;

  try {
    return value.toLocaleString("en-US", {
      style: "currency",
      currency: currency || "USD",
    });
  } catch {
    return `$${displayNumber(value)}`;
  }
};

const displayUserRole = (role) => {
  return (
    role
      ?.replace(/_/g, " ")
      .replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase()) || ""
  );
};

/**
 *
 * @param {*} date
 * @param {boolean} [full]
 * @param {string|null} [separator]
 * @returns
 */
const displayDate = (date, full = false, separator = null) => {
  if (!date) {
    return "";
  }

  const str = new Date(date).toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...(full
      ? {
          hour: "2-digit",
          minute: "2-digit",
        }
      : null),
  });

  if (separator) {
    return str.replaceAll("/", separator);
  }

  return str;
};

const formatDateToTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  let interval = seconds / 31536000;
  if (interval > 1) {
    return (
      Math.floor(interval) +
      " year" +
      (Math.floor(interval) === 1 ? "" : "s") +
      " ago"
    );
  }
  interval = seconds / 2592000;
  if (interval > 1) {
    return (
      Math.floor(interval) +
      " month" +
      (Math.floor(interval) === 1 ? "" : "s") +
      " ago"
    );
  }
  interval = seconds / 86400;
  if (interval > 1) {
    return (
      Math.floor(interval) +
      " day" +
      (Math.floor(interval) === 1 ? "" : "s") +
      " ago"
    );
  }
  interval = seconds / 3600;
  if (interval > 1) {
    return (
      Math.floor(interval) +
      " hour" +
      (Math.floor(interval) === 1 ? "" : "s") +
      " ago"
    );
  }
  interval = seconds / 60;
  if (interval > 1) {
    return (
      Math.floor(interval) +
      " minute" +
      (Math.floor(interval) === 1 ? "" : "s") +
      " ago"
    );
  }
  return (
    Math.floor(seconds) +
    " second" +
    (Math.floor(seconds) === 1 ? "" : "s") +
    " ago"
  );
};

const toSnakeCase = (string) => {
  return string.toLowerCase().replace(" ", "_");
};

const calcCampaignProgress = (campaign) => {
  const defaultProgress = 2;
  const formattedProgress = Math.round(campaign.progress) || defaultProgress;
  return formattedProgress < defaultProgress
    ? defaultProgress
    : formattedProgress;
};

const calcCampaignRemainingMinutes = (campaign) => {
  const { remainingTime } = campaign;
  if (typeof remainingTime === "number") {
    const formattedRemainingTime = Math.floor(remainingTime / 60000);
    return formattedRemainingTime === 0 ? "< 1" : formattedRemainingTime;
  }
  return campaign.contacts?.length < 50 ? "< 1" : "< 5";
};

const buildSequenceTemplateStepName = (step, useTab = false) => {
  const offsetUnitLabel = FSC_STEP_OFFSET_UNIT_LABEL[step.offsetUnit];
  const offsetText = step.offset
    ? ` - Offset ${step.offset}${
        offsetUnitLabel
          ? ` ${offsetUnitLabel}${step.offset > 1 ? "s" : ""}`
          : step.offsetUnit
      }`
    : "";

  const automationLevelLabel =
    step.automationLevel === FSC_STEP_AUTOMATION_LEVEL.AUTOMATIC
      ? "Automated"
      : step.automationLevel;

  return `Step ${step.orderNum}:${useTab ? "\t" : " "}${
    step.type
  } ${automationLevelLabel}${offsetText}`;
};

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}

function removeCookie(name) {
  document.cookie = `${name}=; SameSite=None; Secure; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
}

const getNonConfigurableIntegrations = (companyId) => {
  const obj =
    getObjectFromLocalStorage("NonConfigurableIntegrations") ||
    getObjectFromLocalStorage("IntegrationsForSpreadsheets") ||
    {};
  return obj[companyId];
};

const setNonConfigurableIntegrations = (companyId, data) => {
  const obj =
    getObjectFromLocalStorage("NonConfigurableIntegrations") ||
    getObjectFromLocalStorage("IntegrationsForSpreadsheets") ||
    {};

  if (data && Object.keys(data).length) {
    obj[companyId] = data;
  } else {
    delete obj[companyId];
  }

  saveObjectToLocalStorage("NonConfigurableIntegrations", obj);
  removeDataFromLocalStorage("IntegrationsForSpreadsheets");
};

const isObjectEmpty = (object) => {
  return isEmpty(object);
};

const openPreviewMessageLinkInNewTab = (e) => {
  const targetLink = e?.target?.closest("a");
  if (targetLink) {
    if (targetLink.getAttribute("target") !== "_blank") {
      targetLink.setAttribute("target", "_blank");
    }
    if (targetLink.getAttribute("rel") !== "noopener noreferrer") {
      targetLink.setAttribute("rel", "noopener noreferrer");
    }
  }
};

const saveLastLoginMethod = (oauthType) => {
  saveDataToLocalStorage(LAST_LOGIN_METHOD_LS_KEY, oauthType);
};

export const getCompanyFromLocalStorage = () => {
  const companyInfo = getObjectFromLocalStorage("companyInfo");
  return companyInfo?.company;
};

const checkIsFreePlanForCompany = (company) => {
  return company?.subscriptionPlanName === SUBSCRIPTION_PLAN_NAME.FREE;
};

const isCompanyAdvIntegrationsSettings = (company) => {
  if (!company) {
    company = getCompanyFromLocalStorage();
  }
  return (
    !company?.subscriptionPlanName ||
    company.subscriptionPlanName === SUBSCRIPTION_PLAN_NAME.PROFESSIONAL ||
    company?.perMessagePricing === true
  );
};

const setPostSignUpOnboardingFinishLater = () => {
  saveDataToLocalStorage(
    "isPostSignUpOnboardingFinishLater",
    new Date().valueOf() + 1000 * 60 * 60 * 24,
    true
  );
};

const isPostSignUpOnboardingFinishLater = () => {
  const expiry = getDataFromLocalStorage("isPostSignUpOnboardingFinishLater");
  if (expiry) {
    if (expiry >= new Date().valueOf()) {
      return true;
    }
    removeDataFromLocalStorage("isPostSignUpOnboardingFinishLater", true);
  }
  return false;
};

const isNeedToShowPostSignUpOnboarding = (res) => {
  return (
    res?.result?.postSignUpOnboarding?.completed === false &&
    !isPostSignUpOnboardingFinishLater()
  );
};

const isChatLikeOnboarding = (res) => {
  return (
    res?.result?.postSignUpOnboarding?.completed === false &&
    res?.result?.postSignUpOnboarding?.chatLike === true
  );
};

const isSpreadsheetType = (p) => SPREADSHEET_TYPES.includes(p);

const isNonConfigurableCrm = (p) => isSpreadsheetType(p) || p === CRMS.WINRED;

const sortRows = (rows, sortField, sortOrder, columnRender) => {
  if (!sortField || rows.length <= 1) {
    return rows;
  }

  return rows.sort((u1, u2) => {
    let a = u1[sortField];
    let b = u2[sortField];

    if (columnRender) {
      const ar = columnRender(u1);
      if (typeof ar === "string") {
        a = ar;
      }
      const br = columnRender(u2);
      if (typeof br === "string") {
        b = br;
      }
    }

    if (sortOrder === "desc") {
      const tmp = a;
      a = b;
      b = tmp;
    }

    if (typeof a === "number" || typeof b === "number") {
      return (a || 0) - (b || 0);
    }

    if (typeof a === "boolean" || typeof b === "boolean") {
      return (a || false) === (b || false) ? 0 : a ? -1 : 1;
    }

    return (a || "").localeCompare(b || "");
  });
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const checkIsAnyAdminUserRole = (role) =>
  AS_COMPANY_ADMIN_USER_ROLES.includes(role);

const getUserRoleDisplay = (role) => {
  const roleDisplayMap = {
    [USER_ROLES.DEACTIVATED]: "Deactivated",
    [USER_ROLES.UNPAID]: "Unpaid",
    [USER_ROLES.INVITED]: "Invited",
    [USER_ROLES.ADMIN]: "Admin",
    [USER_ROLES.USER]: "User",
    [USER_ROLES.COMPANY_ADMIN]: "Company Admin",
  };

  return roleDisplayMap[role] || role;
};

const getThemeStyles = ({ theme, salesstreamStyles, reachthepeopleStyles }) => {
  if (salesstreamStyles && theme.isSalesstream) {
    return css`
      ${salesstreamStyles}
    `;
  }

  if (reachthepeopleStyles && theme.isReachthepeople) {
    return css`
      ${reachthepeopleStyles}
    `;
  }
  return css``;
};

const isCommonEmailProvider = (email) => {
  if (!email || typeof email !== "string") return false;

  const domain = email.split("@")[1]?.toLowerCase();
  if (!domain) return false;

  const commonProviders = [
    "gmail.com",
    "yahoo.com",
    "outlook.com",
    "hotmail.com",
    "aol.com",
    "icloud.com",
    "protonmail.com",
    "mail.com",
    "live.com",
    "msn.com",
    "me.com",
    "mac.com",
  ];

  return commonProviders.includes(domain);
};

// Format phone number to +1 (xxx) xxx-xxxx format
const formatPhoneNumber = (phone) => {
  if (!phone) return phone;

  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, "");

  // Extract digits only (remove +)
  const digits = cleaned.replace(/\+/g, "");

  // Handle US numbers (10 or 11 digits)
  if (digits.length === 11 && digits.startsWith("1")) {
    // 11 digits starting with 1: +1XXXXXXXXXX
    const areaCode = digits.slice(1, 4);
    const firstPart = digits.slice(4, 7);
    const secondPart = digits.slice(7, 11);
    return `+1 (${areaCode}) ${firstPart}-${secondPart}`;
  } else if (digits.length === 10) {
    // 10 digits: assume US number without country code
    const areaCode = digits.slice(0, 3);
    const firstPart = digits.slice(3, 6);
    const secondPart = digits.slice(6, 10);
    return `+1 (${areaCode}) ${firstPart}-${secondPart}`;
  }

  // For non-US numbers or invalid formats, return as is
  return phone;
};

/**
 * Determine if campaign is SMS-only (no email steps)
 *
 * @param {*} campaign
 * @returns {boolean}
 */
const checkIsSmsOnlyCampaign = (campaign) => {
  if (
    campaign.isFullSequenceCampaign &&
    campaign.sequenceTemplate?.steps?.length
  ) {
    // For Full Sequence Campaigns: SMS-only if all steps are SMS
    return campaign.sequenceTemplate.steps.every(
      (s) => s.type === FSC_STEP_TYPE.SMS
    );
  }

  // For legacy campaigns: SMS-only if useSMS is true and useEmail is false
  return Boolean(campaign.useSMS && !campaign.useEmail);
};

/**
 * Determine if campaign uses email and/or SMS
 *
 * @param {*} campaign
 * @returns {{ hasEmailStep: boolean, hasSmsStep: boolean }}
 */
const checkCampaignUsesEmailAndOrSMS = (campaign) => {
  let hasEmailStep = false;
  let hasSmsStep = false;

  if (campaign.isFullSequenceCampaign && campaign.sequenceTemplate?.steps) {
    // For Full Sequence Campaigns, check the sequence template steps
    hasEmailStep = campaign.sequenceTemplate.steps.some((s) =>
      [FSC_STEP_TYPE.EMAIL, FSC_STEP_TYPE.MARKETING_EMAIL].includes(s.type)
    );
    hasSmsStep = campaign.sequenceTemplate.steps.some(
      (s) => s.type === FSC_STEP_TYPE.SMS
    );
  } else {
    // For legacy campaigns
    hasSmsStep = campaign.useSMS || false;
    hasEmailStep =
      campaign.useEmail ??
      (!campaign.useSMS ||
        (campaign.smsTiming !== undefined &&
          campaign.attempts > (campaign.useSMS ? 1 : 0)));
  }

  return { hasEmailStep, hasSmsStep };
};

/**
 * @param {string} data
 * @returns {string[]}
 */
const splitCsvContacts = (data) => {
  const arr = data.split(/"\n/);
  const pIdx = arr.length - 1;
  return arr.map((c, i) => (i < pIdx ? `${c}"` : c));
};

export {
  fileToBase64,
  displayNumber,
  displayPercent,
  displayCurrency,
  displayUserRole,
  displayDate,
  formatDateToTimeAgo,
  toSnakeCase,
  calcCampaignProgress,
  calcCampaignRemainingMinutes,
  buildSequenceTemplateStepName,
  getCookie,
  removeCookie,
  getNonConfigurableIntegrations,
  setNonConfigurableIntegrations,
  isObjectEmpty,
  openPreviewMessageLinkInNewTab,
  saveLastLoginMethod,
  checkIsFreePlanForCompany,
  isCompanyAdvIntegrationsSettings,
  setPostSignUpOnboardingFinishLater,
  isNeedToShowPostSignUpOnboarding,
  isChatLikeOnboarding,
  isSpreadsheetType,
  isNonConfigurableCrm,
  sortRows,
  sleep,
  checkIsAnyAdminUserRole,
  getUserRoleDisplay,
  getThemeStyles,
  isCommonEmailProvider,
  formatPhoneNumber,
  checkIsSmsOnlyCampaign,
  checkCampaignUsesEmailAndOrSMS,
  splitCsvContacts,
};
